/**
 * User Management translations for user and user group administration pages.
 * Includes strings for user listing, user details, group management, and permissions.
 */

export const userManagementEn = {
  userManagement: {
    users: {
      labels: {
        firstName: 'First Name',
        lastName: 'Last Name',
        email: 'Email',
        groups: 'Groups',
        lastLogin: 'Last Login',
        primaryAdmin: 'Primary Admin',
      },
      actions: {
        inviteUser: 'Invite User',
        goToUserGroups: 'Go to User Groups',
      },
      tooltips: {
        primaryAdmin: 'Primary Admin',
        assignPrimaryAdmin: 'Assign Primary Admin to another admin user by going to their User Details page.',
      },
      primaryAdminCheckbox: 'Make Primary Admin',
    },
    userDetails: {
      labels: {
        firstName: 'First Name',
        lastName: 'Last Name',
        email: 'Email',
        userGroups: 'User Groups',
        passwordReset: 'Password Reset',
      },
      descriptions: {
        passwordReset: 'Send password reset email, click on the button below.',
      },
      actions: {
        sendResetEmail: 'Send Reset Email',
      },
      dialogs: {
        changePrimaryAdmin: {
          title: 'Change Primary Admin',
          message: 'Are you sure you want to make this Admin user the Primary Admin?',
          confirmButtonText: 'Confirm',
        },
        removeFromAllGroups: {
          title: 'Warning!',
          message: 'Removing this user from all user groups will prevent that user from logging in. Are you sure you want to continue?',
          confirmButtonText: 'Yes, remove this user from all groups',
        },
        deleteUser: {
          title: 'Delete User',
          message: 'Are you sure you would like to delete this user?\nThis will remove the user from all user groups and will not be able to log into this client on the Rentsync Platform.',
        },
      },
      messages: {
        memberSincePrefix: 'Member since:',
        inviteSent: 'Invite Sent',
      },
      tooltips: {
        primaryAdminCannotBeDeleted: 'Primary Admin user cannot be deleted. Assign Primary Admin to another admin user by going to their User Details page.',
      },
      toasts: {
        primaryAdminUpdated: 'Primary Admin user updated!',
        passwordResetSent: 'Password reset email has been sent to {firstName} {lastName}',
        passwordResetSentTitle: 'Email Sent!',
        passwordResetFailed: 'Unable to send reset email, please try again',
        passwordResetFailedTitle: 'Email Failed!',
        saving: 'Saving...',
        userDataSaved: 'User data saved!',
        userDeleted: 'User Deleted!',
        userDeletedMessage: 'User {firstName} {lastName} has been successfully deleted.',
      },
    },
    userGroups: {
      labels: {
        name: 'Group Name',
        groupName: 'Group Name',
        accountModuleCount: 'Account Access',
        propertyModuleCount: 'Property Access',
        siteModuleCount: 'Website Access',
        websites: 'Websites',
      },
      actions: {
        newGroup: 'New Group',
        goToUsers: 'Go to Users',
      },
      access: {
        fullAccess: 'Full Access',
        noAccess: 'No Access',
        someAccess: 'Some Access',
      },
    },
    userGroupDetails: {
      labels: {
        groupName: 'Group Name',
        selectPermissionType: 'Select Permission Type:',
      },
      tabs: {
        account: 'Account',
        properties: 'Properties',
        websites: 'Websites',
      },
      banners: {
        administratorWarning: 'This user group is an Administrator group and cannot have its permissions edited.',
        allPropertiesOverride: 'Users that are in multiple user groups, having both "All Properties Permissions" and "Per Property Permissions", those users will have "All Properties Permissions" override the "Per Property Permissions" permissions',
      },
      sections: {
        allProperties: 'All Properties Permissions',
        customProperties: 'Per Property Permissions',
        allFeatures: 'All Features',
        propertyPermissions: 'Property Permissions ({count})',
        customPropertiesLabel: 'Features',
        websites: 'Websites',
      },
      permissions: {
        allPropertiesLabel: 'All',
        notApplicable: 'N/A',
      },
      dialogs: {
        unsavedChanges: {
          title: 'You have unsaved changes.',
        },
      },
      toasts: {
        groupRemoved: 'Successfully removed this group!',
        groupNotFound: 'Group not found!',
        groupSaved: 'User Group Saved.',
        savingChanges: 'Saving user group changes. This may take a few moments.',
      },
    },
    permissionsAssignment: {
      labels: {
        search: 'Search',
        groupBy: 'Group By',
        propertyCount: '# of Total Properties',
        noAccess: 'No Access',
        viewOnly: 'View Only',
        viewEdit: 'View / Edit',
        viewEditDelete: 'View / Edit / Delete',
      },
      tooltips: {
        premiumFeature: 'This is a premium feature for {count} propert{plural}',
        premiumFeatureGeneral: 'This is a premium feature',
      },
    },
    userList: {
      labels: {
        users: 'Users',
      },
      tooltips: {
        saveRequired: 'Please save for the change to take effect.',
      },
      toasts: {
        userRemoved: 'Successfully removed user from this group!',
      },
    },
    modals: {
      addExistingUser: {
        title: 'Add Existing User',
        searchLabel: 'Users',
        confirmButton: 'Confirm',
      },
      checkboxes: {
        selected: 'Selected',
        selectAll: 'Select All',
        deselectAll: 'Deselect All',
      },
      permissionsFilters: {
        allFilters: 'All Filters',
        remove: 'Remove',
        filter: 'Filter',
        filters: 'Filters',
        filtersApplied: 'filters applied',
        resetToDefault: 'Reset to Default',
        cancel: 'Cancel',
        apply: 'Apply',
        accessLevel: 'Access Level',
        status: 'Status',
        statusOptions: {
          active: 'Active',
          archived: 'Archived',
        },
      },
      propertyPermissionChanged: {
        title: 'Property Permissions will be Changed',
        confirmButton: 'Confirm',
        toPerPropertyMessage: 'Going to "Per-property" permissions will disable all current "All properties" permission access. Are you sure?',
        toAllPropertiesMessage: 'Going back to "All properties" permissions will disable all current custom property permission access. Are you sure?',
      },
      websitePermissionsInfo: {
        title: 'Website Permission States',
        okayButton: 'Okay',
        activeState: {
          title: 'Active State',
          description: 'Website is currently live and running.',
        },
        archivedState: {
          title: 'Archived State',
          description: 'The website is archived. Permission access cannot be edited and can only be viewed.',
        },
      },
      addUser: {
        title: 'Add User',
        existingUser: 'Existing User',
        newUser: 'New User',
      },
      addNewUser: {
        title: 'Add New User',
        confirmButton: 'Confirm',
        userGroup: 'User Group',
        selectPlaceholder: 'Select',
        toasts: {
          requiredFields: 'Email, Group, first, and last name are required!',
          errorTitle: 'Error!',
          userAdded: 'Successfully added a new user to this group!',
        },
      },
      addUserGroup: {
        title: 'Add New User Group',
        confirmButton: 'Confirm',
        name: 'Name',
        selectedCount: '[{count} selected]',
        toasts: {
          groupAdded: 'User Group Added Successfully',
          groupAddedTitle: 'User Group Added!',
        },
      },
      userGroupDeletion: {
        deleteUsersTitle: 'Delete User(s)',
        deleteGroupTitle: 'Delete User Group',
        transferButton: 'Transfer',
        deleteButton: 'Delete',
        singleUserBody: 'This User(s) will not be able to log into this client on the Rentsync Platform. Are you sure? You can transfer the user(s) to another User Group if you want to keep this user(s).',
        groupBody: 'Deleting this User Group will also delete the user(s) listed below permanently and not be able to log into this client on the Rentsync Platform. Are you sure? You can transfer the users to another user group if you want to keep these users.',
        userLabel: 'User #{number}: {firstName} {lastName}',
      },
      userTransfer: {
        title: 'Transfer User(s)',
        transferButton: 'Transfer',
        assignedUsers: 'Assigned User(s):',
        selectedCount: '[{count} Selected]',
        userSingular: '{count} User',
        userPlural: '{count} Users',
      },
    },
    propertyPermissionSelection: {
      label: 'Modify Permissions to:',
      allPropertiesPermissions: 'All Properties Permissions',
      perPropertyPermissions: 'Per Property Permissions',
    },
    groupByOptions: {
      properties: 'Properties',
      features: 'Features',
    },
  },
} as const;

export const userManagementFr = {
  userManagement: {
    users: {
      labels: {
        firstName: 'Prénom',
        lastName: 'Nom',
        email: 'Courriel',
        groups: 'Groupes',
        lastLogin: 'Dernière connexion',
        primaryAdmin: 'Admin principal',
      },
      actions: {
        inviteUser: 'Inviter un utilisateur',
        goToUserGroups: 'Aller aux groupes d\'utilisateurs',
      },
      tooltips: {
        primaryAdmin: 'Admin principal',
        assignPrimaryAdmin: 'Attribuez Admin principal à un autre utilisateur administrateur en accédant à sa page Détails de l\'utilisateur.',
      },
      primaryAdminCheckbox: 'Faire Admin principal',
    },
    userDetails: {
      labels: {
        firstName: 'Prénom',
        lastName: 'Nom',
        email: 'Courriel',
        userGroups: 'Groupes d\'utilisateurs',
        passwordReset: 'Réinitialisation du mot de passe',
      },
      descriptions: {
        passwordReset: 'Envoyer un courriel de réinitialisation du mot de passe, cliquez sur le bouton ci-dessous.',
      },
      actions: {
        sendResetEmail: 'Envoyer l\'e-mail de réinitialisation',
      },
      dialogs: {
        changePrimaryAdmin: {
          title: 'Changer Admin principal',
          message: 'Êtes-vous sûr de vouloir faire de cet utilisateur administrateur l\'Admin principal?',
          confirmButtonText: 'Confirmer',
        },
        removeFromAllGroups: {
          title: 'Avertissement !',
          message: 'Le retrait de cet utilisateur de tous les groupes d\'utilisateurs l\'empêchera de se connecter. Êtes-vous sûr de vouloir continuer?',
          confirmButtonText: 'Oui, retirer cet utilisateur de tous les groupes',
        },
        deleteUser: {
          title: 'Supprimer l\'utilisateur',
          message: 'Êtes-vous sûr de vouloir supprimer cet utilisateur?\nCela retirera l\'utilisateur de tous les groupes d\'utilisateurs et il ne pourra pas se connecter à ce client sur la plateforme Rentsync.',
        },
      },
      messages: {
        memberSincePrefix: 'Membre depuis :',
        inviteSent: 'Invitation envoyée',
      },
      tooltips: {
        primaryAdminCannotBeDeleted: 'L\'Admin principal ne peut pas être supprimé. Attribuez Admin principal à un autre utilisateur administrateur en accédant à sa page Détails de l\'utilisateur.',
      },
      toasts: {
        primaryAdminUpdated: 'Utilisateur Admin principal mis à jour!',
        passwordResetSent: 'L\'e-mail de réinitialisation du mot de passe a été envoyé à {firstName} {lastName}',
        passwordResetSentTitle: 'E-mail envoyé!',
        passwordResetFailed: 'Impossible d\'envoyer l\'e-mail de réinitialisation, veuillez réessayer',
        passwordResetFailedTitle: 'L\'envoi d\'e-mail a échoué!',
        saving: 'Enregistrement...',
        userDataSaved: 'Données utilisateur enregistrées!',
        userDeleted: 'Utilisateur supprimé!',
        userDeletedMessage: 'L\'utilisateur {firstName} {lastName} a été supprimé avec succès.',
      },
    },
    userGroups: {
      labels: {
        name: 'Nom du groupe',
        groupName: 'Nom du groupe',
        accountModuleCount: 'Accès aux comptes',
        propertyModuleCount: 'Accès aux propriétés',
        siteModuleCount: 'Accès au site Web',
        websites: 'Sites Web',
      },
      actions: {
        newGroup: 'Nouveau groupe',
        goToUsers: 'Aller aux utilisateurs',
      },
      access: {
        fullAccess: 'Accès complet',
        noAccess: 'Pas d\'accès',
        someAccess: 'Accès partiel',
      },
    },
    userGroupDetails: {
      labels: {
        groupName: 'Nom du groupe',
        selectPermissionType: 'Sélectionner le type de permission :',
      },
      tabs: {
        account: 'Compte',
        properties: 'Propriétés',
        websites: 'Sites Web',
      },
      banners: {
        administratorWarning: 'Ce groupe d\'utilisateurs est un groupe administrateur et ses autorisations ne peuvent pas être modifiées.',
        allPropertiesOverride: 'Les utilisateurs qui se trouvent dans plusieurs groupes d\'utilisateurs, ayant à la fois des « Autorisations toutes les propriétés » et des « Autorisations par propriété », disposeront des « Autorisations toutes les propriétés » qui remplacent les autorisations « Par propriété »',
      },
      sections: {
        allProperties: 'Autorisations pour toutes les propriétés',
        customProperties: 'Autorisations par propriété',
        allFeatures: 'Toutes les fonctionnalités',
        propertyPermissions: 'Autorisations de propriété ({count})',
        customPropertiesLabel: 'Fonctionnalités',
        websites: 'Sites Web',
      },
      permissions: {
        allPropertiesLabel: 'Tous',
        notApplicable: 'S/O',
      },
      dialogs: {
        unsavedChanges: {
          title: 'Vous avez des modifications non enregistrées.',
        },
      },
      toasts: {
        groupRemoved: 'Groupe supprimé avec succès!',
        groupNotFound: 'Groupe non trouvé!',
        groupSaved: 'Groupe d\'utilisateurs enregistré.',
        savingChanges: 'Enregistrement des modifications du groupe d\'utilisateurs. Cela peut prendre quelques instants.',
      },
    },
    permissionsAssignment: {
      labels: {
        search: 'Rechercher',
        groupBy: 'Grouper par',
        propertyCount: '# du nombre total de propriétés',
        noAccess: 'Aucun accès',
        viewOnly: 'Lecture seule',
        viewEdit: 'Lecture / Modification',
        viewEditDelete: 'Lecture / Modification / Suppression',
      },
      tooltips: {
        premiumFeature: 'Ceci est une fonctionnalité premium pour {count} propriét{plural}',
        premiumFeatureGeneral: 'Ceci est une fonctionnalité premium',
      },
    },
    userList: {
      labels: {
        users: 'Utilisateurs',
      },
      tooltips: {
        saveRequired: 'Veuillez enregistrer pour que la modification prenne effet.',
      },
      toasts: {
        userRemoved: 'Utilisateur retiré de ce groupe avec succès!',
      },
    },
    modals: {
      addExistingUser: {
        title: 'Ajouter un utilisateur existant',
        searchLabel: 'Utilisateurs',
        confirmButton: 'Confirmer',
      },
      checkboxes: {
        selected: 'Sélectionné',
        selectAll: 'Tout sélectionner',
        deselectAll: 'Tout désélectionner',
      },
      permissionsFilters: {
        allFilters: 'Tous les filtres',
        remove: 'Retirer',
        filter: 'Filtre',
        filters: 'Filtres',
        filtersApplied: 'filtres appliqués',
        resetToDefault: 'Réinitialiser par défaut',
        cancel: 'Annuler',
        apply: 'Appliquer',
        accessLevel: 'Niveau d\'accès',
        status: 'Statut',
        statusOptions: {
          active: 'Actif',
          archived: 'Archivé',
        },
      },
      propertyPermissionChanged: {
        title: 'Les autorisations de propriété seront modifiées',
        confirmButton: 'Confirmer',
        toPerPropertyMessage: 'Passer aux autorisations « Par propriété » désactivera tous les accès actuels aux autorisations « Toutes les propriétés ». Êtes-vous sûr?',
        toAllPropertiesMessage: 'Revenir aux autorisations « Toutes les propriétés » désactivera tous les accès aux autorisations de propriété personnalisées actuelles. Êtes-vous sûr?',
      },
      websitePermissionsInfo: {
        title: 'États des autorisations du site Web',
        okayButton: 'D\'accord',
        activeState: {
          title: 'État actif',
          description: 'Le site Web est actuellement en ligne et en cours d\'exécution.',
        },
        archivedState: {
          title: 'État archivé',
          description: 'Le site Web est archivé. L\'accès aux autorisations ne peut pas être modifié et peut seulement être consulté.',
        },
      },
      addUser: {
        title: 'Ajouter un utilisateur',
        existingUser: 'Utilisateur existant',
        newUser: 'Nouvel utilisateur',
      },
      addNewUser: {
        title: 'Ajouter un nouvel utilisateur',
        confirmButton: 'Confirmer',
        userGroup: 'Groupe d\'utilisateurs',
        selectPlaceholder: 'Sélectionner',
        toasts: {
          requiredFields: 'Le courriel, le groupe, le prénom et le nom sont obligatoires!',
          errorTitle: 'Erreur!',
          userAdded: 'Nouvel utilisateur ajouté à ce groupe avec succès!',
        },
      },
      addUserGroup: {
        title: 'Ajouter un nouveau groupe d\'utilisateurs',
        confirmButton: 'Confirmer',
        name: 'Nom',
        selectedCount: '[{count} sélectionné(s)]',
        toasts: {
          groupAdded: 'Groupe d\'utilisateurs ajouté avec succès',
          groupAddedTitle: 'Groupe d\'utilisateurs ajouté!',
        },
      },
      userGroupDeletion: {
        deleteUsersTitle: 'Supprimer le(s) utilisateur(s)',
        deleteGroupTitle: 'Supprimer le groupe d\'utilisateurs',
        transferButton: 'Transférer',
        deleteButton: 'Supprimer',
        singleUserBody: 'Ce(s) utilisateur(s) ne pourra(ont) plus se connecter à ce client sur la plateforme Rentsync. Êtes-vous sûr? Vous pouvez transférer le(s) utilisateur(s) vers un autre groupe d\'utilisateurs si vous souhaitez le(s) conserver.',
        groupBody: 'La suppression de ce groupe d\'utilisateurs supprimera également définitivement le(s) utilisateur(s) répertorié(s) ci-dessous, qui ne pourront plus se connecter à ce client sur la plateforme Rentsync. Êtes-vous sûr? Vous pouvez transférer les utilisateurs vers un autre groupe d\'utilisateurs si vous souhaitez les conserver.',
        userLabel: 'Utilisateur n° {number} : {firstName} {lastName}',
      },
      userTransfer: {
        title: 'Transférer le(s) utilisateur(s)',
        transferButton: 'Transférer',
        assignedUsers: 'Utilisateur(s) assigné(s) :',
        selectedCount: '[{count} sélectionné(s)]',
        userSingular: '{count} utilisateur',
        userPlural: '{count} utilisateurs',
      },
    },
    propertyPermissionSelection: {
      label: 'Modifier les autorisations en :',
      allPropertiesPermissions: 'Autorisations pour toutes les propriétés',
      perPropertyPermissions: 'Autorisations par propriété',
    },
    groupByOptions: {
      properties: 'Propriétés',
      features: 'Fonctionnalités',
    },
  },
} as const;
